/**
 * Which document sets contain this document.
 *
 * The question a reviser actually asks is not "where is this filed" but "what
 * breaks if I change it". So each row says whether that master is AFFECTED —
 * bound to latest-approved and therefore tracking this document — or pinned
 * and insulated. That distinction is the whole value of the panel.
 */
import React from 'react'

import type { DmsWhereUsedRow } from './contracts'

const MUTED = 'var(--text-muted, #667085)'
const WARN = 'var(--warning, #9a6700)'

export interface WhereUsedPanelProps {
  rows: DmsWhereUsedRow[]
  hidden?: number
  title?: string
}

export function WhereUsedPanel({ rows, hidden = 0,
                                 title = 'Used in' }: WhereUsedPanelProps) {
  const [open, setOpen] = React.useState(false)
  if (!rows.length && !hidden) return null

  const affected = rows.filter((r) => r.affected).length

  return (
    <section className="dms-where-used" style={{ marginTop: 18 }}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        style={{ background: 'none', border: 0, padding: 0, cursor: 'pointer',
                 font: 'inherit', display: 'flex', gap: 6, alignItems: 'center' }}
      >
        <span style={{ fontSize: 14, textTransform: 'uppercase',
                       letterSpacing: 0.4 }}>
          {title} {rows.length} set{rows.length === 1 ? '' : 's'}
        </span>
        <span style={{ color: MUTED }}>{open ? '▾' : '▸'}</span>
      </button>

      {affected > 0 && (
        <p style={{ fontSize: 13, color: WARN, margin: '4px 0 0' }}>
          {affected} of them follow this document's latest approved version and
          will change when it is re-approved.
        </p>
      )}

      {open && (
        <ul style={{ listStyle: 'none', padding: 0, margin: '8px 0 0' }}>
          {rows.map((row) => (
            <li key={`${row.parent_id}-${row.depth}`}
                style={{ padding: '5px 0', paddingLeft: (row.depth - 1) * 14,
                         borderTop: '1px solid var(--border, #e4e7ec)',
                         display: 'flex', gap: 8, alignItems: 'baseline' }}>
              <span style={{ flex: 1 }}>{row.title}</span>
              <span style={{ fontSize: 13, color: MUTED }}>{row.state}</span>
              <span style={{ fontSize: 13,
                             color: row.affected ? WARN : MUTED }}>
                {row.affected ? 'Follows this' : 'Pinned'}
              </span>
            </li>
          ))}
        </ul>
      )}

      {hidden > 0 && (
        <p style={{ fontSize: 13, color: MUTED, marginTop: 6 }}>
          {hidden} further set{hidden === 1 ? '' : 's'} you are not able to view.
        </p>
      )}
    </section>
  )
}

export default WhereUsedPanel
